import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, useMatches } from "@tanstack/react-router";
import { StorefrontNuqsAdapter } from "~/app/router/storefrontNuqsAdapter";
import { useRoutePageAnalyticsContext } from "~/app/analytics/pageContext/useRoutePageAnalyticsContext";
import type { PageMetadata } from "~/app/api/pageData";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import type { StorefrontShellVariant } from "~/app/shell/types";
import CheckoutHeader from "~/features/Checkout/components/CheckoutHeader";
import { StaticPageHeader } from "~/features/navigation/components/StaticPageHeader/StaticPageHeader";
import { TimelineConfiguratorHeader } from "./$productCode/configuratorPage/-configuratorShell";
import { findConfiguratorRegistration } from "~/bridges/configurator/configuratorRegistry";

export interface StorefrontRouterContext {
  queryClient: QueryClient;
}

/**
 * Per-route shell overrides (user ruling 15): these routes customized the
 * retired per-route StorefrontShellFrame wrapper. The values are verbatim
 * from the pre-migration route files; route ids match the generated tree.
 */
export const ROUTE_SHELL_OVERRIDES: Record<
  string,
  { Header?: () => React.ReactNode; hideFooter?: boolean; variant?: StorefrontShellVariant }
> = {
  "/checkout/multi/summary/view": { Header: CheckoutHeader },
  "/static/welcome": { Header: StaticPageHeader, hideFooter: true },
  "/datasheet/$productDesignation": { Header: StaticPageHeader },
};

export const ROUTE_VARIANT_OVERRIDES: Record<string, StorefrontShellVariant> = {
  // embeddedProductConfigurator hardcoded variant="embedded" on its wrapper.
  "/embeddedProductConfigurator/$productCode": "embedded",
};

// The four LinearConfigurator families default the templateUid: the dedicated
// route knows its template, and the page-data metadata can arrive as the
// withoutResolvedPage fallback (null templateUid) at runtime.
export const CONFIGURATOR_ROUTE_IDS = new Set([
  "/$productCode/configuratorPage/LinearConfigurator",
  "/rails/$productCode/railsConfiguratorPage/LinearConfigurator",
  "/carriages/$productCode/carriagesConfiguratorPage/LinearConfigurator",
  "/shaft/$productCode/shaftConfiguratorPage/LinearConfigurator",
]);
const CONFIGURATOR_FALLBACK_TEMPLATE_UID = "ProductConfigurationTemplate";

/**
 * Extracts the leaf route's page-data metadata from its loader result. Loader
 * shapes differ per route family: flat `{metadata}` (most), `{page, pageData}`
 * (cart), `{pageType, result}` (CMS catch-all). A route without metadata
 * (e.g. the wishlist deep links) never rendered a shell pre-migration and
 * still does not: the frame is conditional on metadata being present.
 */
function extractLeafMetadata(loaderData: unknown): PageMetadata | undefined {
  if (typeof loaderData !== "object" || loaderData === null) {
    return undefined;
  }
  const record = loaderData as Record<string, unknown>;
  // Flat shape (most routes): the loader returns { data, metadata } directly.
  if (typeof record.metadata === "object" && record.metadata !== null) {
    return record.metadata as PageMetadata;
  }
  // Wrapped shapes: cart { page, pageData }, CMS catch-all { pageType, result }.
  for (const key of ["pageData", "result"]) {
    const wrapper = record[key];
    if (typeof wrapper === "object" && wrapper !== null) {
      const metadata = (wrapper as Record<string, unknown>).metadata;
      if (metadata && typeof metadata === "object") {
        return metadata as PageMetadata;
      }
    }
  }
  return undefined;
}

export interface ShellPresentation {
  variant: StorefrontShellVariant;
  metadata: PageMetadata;
  header: React.ReactNode;
  hideFooter: boolean | undefined;
}

/**
 * Pure presentation decision for the shell frame of the leaf route: which
 * header replaces the standard chrome, whether the footer is hidden, which
 * variant renders, and the (templateUid-defaulted) metadata. Returns undefined
 * when the route renders no shell frame at all (no page-data metadata).
 */
export function resolveShellPresentation(
  routeId: string,
  params: Record<string, unknown>,
  metadata: PageMetadata | undefined,
  shellProps: { variant: StorefrontShellVariant },
): ShellPresentation | undefined {
  const override = ROUTE_SHELL_OVERRIDES[routeId];
  const variant = ROUTE_VARIANT_OVERRIDES[routeId] || shellProps.variant;
  let resolvedMetadata = metadata;
  let header: React.ReactNode = override?.Header ? <override.Header /> : undefined;

  // Configurator: timeline registrations replace the standard header, and the
  // templateUid gets its dedicated-route default (verbatim pre-migration).
  if (CONFIGURATOR_ROUTE_IDS.has(routeId)) {
    resolvedMetadata = resolvedMetadata
      ? {
          ...resolvedMetadata,
          templateUid: resolvedMetadata.templateUid ?? CONFIGURATOR_FALLBACK_TEMPLATE_UID,
        }
      : resolvedMetadata;
    const productCode = (params as { productCode?: string }).productCode;
    const registration = productCode ? findConfiguratorRegistration(productCode) : undefined;
    if (registration?.engine === "timeline") {
      header = <TimelineConfiguratorHeader definition={registration.definition} />;
    }
  }

  if (!resolvedMetadata) {
    // Pre-migration, routes without page-data metadata rendered no shell frame.
    return undefined;
  }

  return { variant, metadata: resolvedMetadata, header, hideFooter: override?.hideFooter };
}

// One nuqs adapter for the whole router tree (task 0231, D1/ADR 0001): the
// storefront tanstack-router adapter derives its search params from the
// router location, so every route-level consumer sees router navigations —
// programmatic same-route `?text=` changes update a mounted page instead of
// remounting it. The adapter is the OWNED wrapper (Q9a): its updateUrl
// percent-encodes raw pipes in the rendered query — the only single-encoding
// seam (probe: `0231-url-state-architecture-artifacts/
// probe-pipe-encoder-variant.md`) — replacing the deleted global
// searchUrlSanitizer history patch. Per-page plain adapters still override
// this provider inside their own subtrees until slices 3+ migrate them.
function StorefrontRootRoute() {
  // Page-scoped analytics context (task 0061): the page-context channel's
  // pageCategory + the per-route virtualPageView dataLayer push, on every
  // committed navigation.
  useRoutePageAnalyticsContext();

  const shellProps = useStorefrontShellProps();
  const matches = useMatches();
  const last = matches[matches.length - 1];
  const presentation = last
    ? resolveShellPresentation(last.routeId, last.params, extractLeafMetadata(last.loaderData), shellProps)
    : undefined;

  return (
    <StorefrontNuqsAdapter>
      {presentation ? (
        <StorefrontShellFrame
          variant={presentation.variant}
          metadata={presentation.metadata}
          header={presentation.header}
          hideFooter={presentation.hideFooter}
        >
          <Outlet />
        </StorefrontShellFrame>
      ) : (
        <Outlet />
      )}
    </StorefrontNuqsAdapter>
  );
}

export const Route = createRootRouteWithContext<StorefrontRouterContext>()({
  component: StorefrontRootRoute,
});
